import type { CloudStorageApi } from "./cloud-storage-api.js";
import { deriveTitleAndPreview } from "./extract-text.js";
import {
  DraftQuotaExceededError,
  type DraftMeta,
  type DraftRecord,
  type DraftStorage,
  type PersistedMedia,
  type SerializedEditorStateJson,
} from "./types.js";

const INDEX_KEY = "drafts_index";
/** Each CloudStorage value is capped at 4096 bytes. We chunk well under. */
const CHUNK_SIZE = 3500;
/** Telegram CloudStorage cap is 1024 keys per user. */
const KEY_BUDGET = 1024;
const KEY_BUDGET_WARNING_AT = 800;

// Telegram CloudStorage only accepts keys matching [A-Za-z0-9_-]{1,128}.
// Colons get rejected with STORAGE_KEY_INVALID, so we use underscores as
// the delimiter. UUID hyphens are allowed and stay intact for grep-ability.
const chunkKey = (id: string, i: number): string =>
  `draft_${id}_${String(i)}`;

export interface CloudStorageDraftsOptions {
  api: CloudStorageApi;
  /** Override for tests. */
  now?: () => Date;
}

export class CloudStorageDrafts implements DraftStorage {
  private readonly api: CloudStorageApi;
  private readonly now: () => Date;

  constructor(opts: CloudStorageDraftsOptions) {
    this.api = opts.api;
    this.now = opts.now ?? (() => new Date());
  }

  async list(): Promise<DraftMeta[]> {
    const idx = await this.readIndex();
    return [...idx.drafts].sort((a, b) =>
      b.updated_at.localeCompare(a.updated_at),
    );
  }

  async load(id: string): Promise<DraftRecord | null> {
    const idx = await this.readIndex();
    const meta = idx.drafts.find((d) => d.id === id);
    if (!meta) return null;

    const chunks: string[] = [];
    for (let i = 0; i < meta.chunk_count; i++) {
      const chunk = await this.api.getItem(chunkKey(id, i));
      if (chunk == null) {
        throw new Error(`Missing chunk ${String(i)} of draft ${id}`);
      }
      chunks.push(chunk);
    }
    const json = chunks.join("");
    const parsed = JSON.parse(json) as unknown;
    // Legacy drafts stored the raw editor state (recognisable by a
    // top-level `root` field). Newer drafts wrap state + optional media
    // in an envelope.
    if (parsed && typeof parsed === "object" && "root" in parsed) {
      return { meta, state: parsed as SerializedEditorStateJson };
    }
    const env = parsed as {
      state: SerializedEditorStateJson;
      media?: PersistedMedia;
    };
    return env.media
      ? { meta, state: env.state, media: env.media }
      : { meta, state: env.state };
  }

  async save(
    id: string,
    state: SerializedEditorStateJson,
    titleHint: string,
    media?: PersistedMedia | null,
  ): Promise<DraftMeta> {
    const payload = media ? { state, media } : { state };
    const json = JSON.stringify(payload);
    const chunks: string[] = [];
    for (let i = 0; i < json.length; i += CHUNK_SIZE) {
      chunks.push(json.slice(i, i + CHUNK_SIZE));
    }
    if (chunks.length === 0) chunks.push("");

    const idx = await this.readIndex();
    const existing = idx.drafts.find((d) => d.id === id);

    // Reject early if the new draft would push us over Telegram's key budget.
    const projectedKeyDelta =
      chunks.length - (existing ? existing.chunk_count : 0);
    if (idx.usedKeyCount + projectedKeyDelta > KEY_BUDGET) {
      throw new DraftQuotaExceededError();
    }

    for (let i = 0; i < chunks.length; i++) {
      await this.api.setItem(chunkKey(id, i), chunks[i] ?? "");
    }
    if (existing && existing.chunk_count > chunks.length) {
      const stale = Array.from(
        { length: existing.chunk_count - chunks.length },
        (_, k) => chunkKey(id, chunks.length + k),
      );
      await this.api.removeItems(stale);
    }

    const nowIso = this.now().toISOString();
    const { title, preview } = deriveTitleAndPreview(state);
    // Once the user has manually renamed a draft we keep their title — only
    // auto-derive while the title is still the default. Mirrors desktop's
    // `title = CASE WHEN title = 'Untitled' THEN derived ELSE title END`.
    const titleLocked = existing?.title_locked === true;
    const finalTitle = titleLocked
      ? existing.title
      : (titleHint.trim() || title).slice(0, 60) || "Untitled";

    const meta: DraftMeta = {
      id,
      title: finalTitle,
      preview,
      updated_at: nowIso,
      created_at: existing?.created_at ?? nowIso,
      chunk_count: chunks.length,
      ...(titleLocked ? { title_locked: true } : {}),
    };

    const remaining = idx.drafts.filter((d) => d.id !== id);
    remaining.unshift(meta);
    await this.api.setItem(INDEX_KEY, JSON.stringify(remaining));

    return meta;
  }

  async rename(id: string, title: string): Promise<DraftMeta> {
    const idx = await this.readIndex();
    const existing = idx.drafts.find((d) => d.id === id);
    if (!existing) {
      throw new Error(`Draft ${id} not found`);
    }
    const trimmed = title.trim().slice(0, 60) || "Untitled";
    const updated: DraftMeta = {
      ...existing,
      title: trimmed,
      title_locked: true,
      updated_at: this.now().toISOString(),
    };
    const remaining = idx.drafts.filter((d) => d.id !== id);
    remaining.unshift(updated);
    await this.api.setItem(INDEX_KEY, JSON.stringify(remaining));
    return updated;
  }

  async delete(id: string): Promise<void> {
    const idx = await this.readIndex();
    const meta = idx.drafts.find((d) => d.id === id);
    if (!meta) return;
    const keys = Array.from({ length: meta.chunk_count }, (_, i) =>
      chunkKey(id, i),
    );
    await this.api.removeItems(keys);
    const remaining = idx.drafts.filter((d) => d.id !== id);
    await this.api.setItem(INDEX_KEY, JSON.stringify(remaining));
  }

  /**
   * Returns a soft-warning flag when we're getting close to Telegram's
   * 1024-key cap. UI can use this to nudge the user before save fails.
   */
  async quotaStatus(): Promise<{
    used: number;
    cap: number;
    warning: boolean;
  }> {
    const idx = await this.readIndex();
    return {
      used: idx.usedKeyCount,
      cap: KEY_BUDGET,
      warning: idx.usedKeyCount >= KEY_BUDGET_WARNING_AT,
    };
  }

  private async readIndex(): Promise<{
    drafts: DraftMeta[];
    usedKeyCount: number;
  }> {
    const raw = await this.api.getItem(INDEX_KEY);
    if (!raw) {
      return { drafts: [], usedKeyCount: 0 };
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Corrupt index — start clean rather than crashing the app.
      return { drafts: [], usedKeyCount: 0 };
    }
    if (!Array.isArray(parsed)) {
      return { drafts: [], usedKeyCount: 0 };
    }
    const drafts = parsed as DraftMeta[];
    const chunkSum = drafts.reduce((s, d) => s + d.chunk_count, 0);
    // +1 for the index key itself.
    return { drafts, usedKeyCount: chunkSum + 1 };
  }
}

// Re-export the type so consumers don't have to import from two paths.
export type { DraftMeta, DraftRecord, DraftStorage } from "./types.js";
